import { useState, useCallback } from "react";
import {
  useTargetUniverseStats,
  useTargetUniverseFilterOptions,
  useTargetUniverseKanbanData,
  type TargetUniverseFilters,
  type TargetUniverseAccount,
} from "@/hooks/useTargetUniverse";

import { TargetUniverseFilterSidebar } from "@/components/targeting/TargetUniverseFilters";
import { TargetUniverseKanban } from "@/components/targeting/TargetUniverseKanban";
import { AccountReviewPanel } from "@/components/targeting/AccountReviewPanel";

const DEFAULT_FILTERS: TargetUniverseFilters = {
  search: "",
  category: [],
  subcategory: [],
  hasContacts: null,
  assignedTo: [],
  country: [],
  territory: [],
  rep: [],
  carrModality: [],
  modality: [],
  therapeuticArea: [],
  clinicalPhase: [],
  fundingStatus: [],
  employeeRange: [],
  accountTier: [],
  icpTier: [],
  topAccount: false,
};

const TargetUniverse = () => {
  const [filters, setFilters] = useState<TargetUniverseFilters>(DEFAULT_FILTERS);
  const [reviewAccount, setReviewAccount] = useState<TargetUniverseAccount | null>(null);
  const [tierLimits, setTierLimits] = useState<Record<string, number>>({});

  const { data: stats } = useTargetUniverseStats();
  const { data: kanbanData, isLoading: kanbanLoading } = useTargetUniverseKanbanData(filters, tierLimits);
  const { data: filterOptions } = useTargetUniverseFilterOptions();

  const totalCount = stats?.total_accounts ?? 0;

  const handleFiltersChange = (newFilters: TargetUniverseFilters) => {
    setFilters(newFilters);
    setTierLimits({});
  };

  const handleLoadMore = useCallback((tier: string) => {
    setTierLimits((prev) => ({
      ...prev,
      [tier]: (prev[tier] || 50) + 50,
    }));
  }, []);

  return (
    <div className="h-full flex flex-col">
      {/* Page Header */}
      <div className="flex items-center justify-between p-4 border-b bg-background">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Target Universe</h1>
          <p className="text-sm text-muted-foreground">
            {totalCount.toLocaleString()} accounts · Pre-pipeline command center
          </p>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Filter Sidebar */}
        <TargetUniverseFilterSidebar
          totalCount={totalCount}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          filterOptions={filterOptions}
        />

        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          <TargetUniverseKanban
            kanbanData={kanbanData}
            isLoading={kanbanLoading}
            onReview={setReviewAccount}
            stats={stats}
            onLoadMore={handleLoadMore}
          />
        </div>
      </div>

      {/* Review Panel */}
      {reviewAccount && (
        <>
          <div
            className="fixed inset-0 bg-black/20 z-40"
            onClick={() => setReviewAccount(null)}
          />
          <AccountReviewPanel
            account={reviewAccount}
            onClose={() => setReviewAccount(null)}
          />
        </>
      )}
    </div>
  );
};

export default TargetUniverse;
